import React from "react";
import { IResourceComponentsProps } from "@refinedev/core";
import { useTable } from "@refinedev/react-table";
import { ColumnDef } from "@tanstack/react-table";
import {
  ScrollArea,
  Table,
  Pagination,
  Flex,
  LoadingOverlay,
  Text,
  Badge,
  Code,
  Select,
} from "@mantine/core";
import {
  List,
  EditButton,
  ShowButton,
  DeleteButton,
  DateField,
} from "@refinedev/mantine";

import type { Header } from "@tanstack/react-table";
import { DateRangePicker } from "@mantine/dates";
import usePlanPermissions from "./usePlanPermissions";
import AppliedFilters from "components/common/list/AppliedFilters";
import Headers from "components/common/list/Headers";
import { Rows } from "components/common/list/Rows";
import { Plan } from "entities/plan";
import { formatDistanceToNow } from "date-fns";
import { colorsByKind, selectKinds } from "entities/plan/kind";
import { colorsByBrand, selectBrands } from "entities/plan/brand";

export const PlanList: React.FC<IResourceComponentsProps> = () => {
  const { canCreate, canEdit, canDelete } = usePlanPermissions();

  const columns = React.useMemo<ColumnDef<Plan>[]>(
    () => [
      {
        id: "id",
        accessorKey: "id",
        header: "Id",
      },
      {
        id: "remote_id",
        accessorKey: "remote_id",
        header: "Remote ID",
        cell: ({ getValue }) => {
          const remoteId = getValue<Plan["remote_id"]>();
          return <Code>{remoteId}</Code>;
        },
        meta: {
          filterOperator: "contains",
        },
      },
      {
        id: "name",
        accessorKey: "name",
        header: "Name",
        meta: {
          filterOperator: "contains",
        },
      },
      {
        id: "description",
        accessorKey: "description",
        header: "Description",
        cell: ({ getValue }) => {
          const description = getValue<string>();
          return (
            <Text size="sm" lineClamp={2}>
              {description}
            </Text>
          );
        },
        meta: {
          filterOperator: "contains",
        },
      },
      {
        id: "price",
        accessorKey: "price",
        header: "Price",
        cell: ({ getValue }) => {
          const price = getValue<number>();
          return <Text>${(price / 100).toFixed(2)}</Text>;
        },
      },
      {
        id: "period",
        accessorKey: "period",
        header: "Period (months)",
        cell: ({ getValue }) => {
          const period = getValue<Plan["period"]>();
          return <Badge color="blue">{period}</Badge>;
        },
      },
      {
        id: "kind",
        accessorKey: "kind",
        header: "Kind",
        cell: ({ getValue }) => {
          const kind = getValue<Plan["kind"]>();
          return <Badge color={colorsByKind[kind]}>{kind}</Badge>;
        },
        meta: {
          filterElement: ({ header }: { header: Header<Plan, any> }) => {
            return (
              <div>
                <Select
                  placeholder="Kind"
                  data={selectKinds}
                  onChange={header.column.setFilterValue}
                  clearable
                />
              </div>
            );
          },
        },
      },
      {
        id: "brand",
        accessorKey: "brand",
        header: "Brand",
        cell: ({ getValue }) => {
          const brand = getValue<Plan["brand"]>();
          return <Badge color={colorsByBrand[brand]}>{brand}</Badge>;
        },
        meta: {
          filterElement: ({ header }: { header: Header<Plan, any> }) => {
            return (
              <div>
                <Select
                  placeholder="Brand"
                  data={selectBrands}
                  onChange={header.column.setFilterValue}
                  clearable
                />
              </div>
            );
          },
        },
      },
      {
        id: "last_synced_at",
        accessorKey: "last_synced_at",
        header: "Last Synced At",
        cell: function render({ getValue }) {
          const lastSyncedAt = getValue<Date>();
          if (!lastSyncedAt) return <Text color="gray">--</Text>;
          return <Text>{formatDistanceToNow(new Date(lastSyncedAt))}</Text>;
        },
        meta: {
          filterElement: ({ header }: { header: Header<Plan, any> }) => {
            return (
              <div>
                <DateRangePicker
                  type="range"
                  placeholder="Pick dates range"
                  onChange={header.column.setFilterValue}
                  mx="auto"
                  maw={400}
                  withinPortal
                />
              </div>
            );
          },
        },
      },
      {
        id: "last_synced_at",
        accessorKey: "last_synced_at",
        header: "Last Synced At",
        cell: function render({ getValue }) {
          return (
            <Text>
              {formatDistanceToNow(
                new Date(getValue<Plan["last_synced_at"]>()),
              )}
            </Text>
          );
        },
        meta: {
          filterElement: ({ header }: { header: Header<Plan, any> }) => {
            return (
              <div>
                <DateRangePicker
                  type="range"
                  placeholder="Pick dates range"
                  onChange={header.column.setFilterValue}
                  mx="auto"
                  maw={400}
                  withinPortal
                />
              </div>
            );
          },
        },
      },
      {
        id: "actions",
        cell: function render({ row: { original: plan } }) {
          return (
            <Flex columnGap={4}>
              <ShowButton hideText recordItemId={plan.id} />
              {canEdit && <EditButton hideText recordItemId={plan.id} />}
              {canDelete && <DeleteButton hideText recordItemId={plan.id} />}
            </Flex>
          );
        },
        meta: {
          isPlaceholder: true,
        },
      },
    ],
    [canCreate, canEdit, canDelete],
  );

  const {
    getHeaderGroups,
    getRowModel,
    setOptions,
    getFlatHeaders,
    refineCore: {
      setCurrent,
      pageCount,
      current,
      tableQueryResult: { data: tableData, isFetching },
    },
  } = useTable({
    columns,
    refineCoreProps: {
      pagination: {
        pageSize: 50,
      },
    },
  });

  setOptions((prev) => ({
    ...prev,
    meta: {
      ...prev.meta,
    },
  }));

  return (
    <List canCreate={canCreate}>
      <AppliedFilters<Plan> headers={getFlatHeaders()} />
      <ScrollArea>
        <Table highlightOnHover pos="relative">
          <LoadingOverlay visible={isFetching} />
          <Headers<Plan> headerGroups={getHeaderGroups()} />
          <Rows<Plan> rows={getRowModel().rows} />
        </Table>
      </ScrollArea>
      <br />
      <Pagination
        position="right"
        total={pageCount}
        page={current}
        onChange={setCurrent}
      />
    </List>
  );
};
